import { Link } from "react-router-dom";
import { ArrowRight, Check, Hammer, Droplets, Layers, Wrench, Building2, ClipboardCheck, MapPin } from "lucide-react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Seo } from "@/components/layout/Seo";
import { PageHero } from "@/components/layout/PageHero";
import { Reveal, Stagger, StaggerItem } from "@/components/motion/Reveal";
import { FinalCta } from "@/components/home/Sections";
import { SERVICES, FAQ } from "@/data/services";
import { SITE } from "@/config/site";

const ICONS = { Hammer, Droplets, Layers, Wrench, Building2, ClipboardCheck };

const faqLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: FAQ.map((f) => ({ "@type": "Question", name: f.q, acceptedAnswer: { "@type": "Answer", text: f.a } })),
};
const servicesLd = SERVICES.map((s) => ({
  "@context": "https://schema.org",
  "@type": "Service",
  name: s.title,
  description: s.description,
  provider: { "@type": "HomeAndConstructionBusiness", name: SITE.name, url: `${SITE.url}/` },
  areaServed: "Île-de-France",
  url: `${SITE.url}/services#${s.slug}`,
}));
const JSON_LD = [faqLd, ...servicesLd];

export default function Services() {
  return (
    <>
      <Seo
        title="Nos prestations — Maçonnerie, étanchéité, isolation | O'RIZON 360"
        description="Maçonnerie, étanchéité, isolation, équipements techniques, rénovation et entretien d'immeubles en Île-de-France. Devis gratuit sous 48 h ouvrées, garanties légales."
        jsonLd={JSON_LD}
      />
      <PageHero
        crumb="Services"
        eyebrow="Nos prestations"
        lines={["Six métiers du bâtiment,", "une exigence commune"]}
        intro="Des travaux de gros œuvre aux finitions, O'RIZON 360 intervient sur l'ensemble du bâti avec un interlocuteur unique et des devis transparents."
      />

      <nav aria-label="Sommaire des services" className="bg-white border-b border-line sticky top-[76px] z-30 overflow-x-auto" data-testid="services-subnav">
        <div className="container-x flex gap-2 py-3 whitespace-nowrap">
          {SERVICES.map((s) => (
            <a key={s.slug} href={`#${s.slug}`} className="px-4 py-2 rounded-full text-sm font-semibold text-slate bg-concrete hover:bg-copper hover:text-white transition-colors" data-testid={`services-subnav-${s.slug}`}>{s.title}</a>
          ))}
        </div>
      </nav>

      <section className="section bg-concrete">
        <div className="container-x space-y-24">
          {SERVICES.map((s, i) => {
            const Icon = ICONS[s.icon];
            const reverse = i % 2 === 1;
            return (
              <article key={s.slug} id={s.slug} className="scroll-mt-40 grid lg:grid-cols-12 gap-10 items-center" data-testid={`service-section-${s.slug}`}>
                <Reveal className={`lg:col-span-5 ${reverse ? "lg:order-2" : ""}`}>
                  <div className="relative">
                    {/* Image d'illustration — remplacer par une photo de chantier : /public/images/services/{slug}.jpg */}
                    <img src={s.image} alt={`${s.title} — visuel d'illustration`} className="rounded-3xl aspect-[4/3] w-full object-cover" loading="lazy" width="1200" height="900" />
                    <span className="absolute -bottom-5 left-6 w-16 h-16 rounded-2xl bg-copper text-white flex items-center justify-center shadow-xl"><Icon size={28} strokeWidth={1.75} /></span>
                  </div>
                </Reveal>
                <div className={`lg:col-span-7 ${reverse ? "lg:order-1 lg:pr-10" : "lg:pl-10"}`}>
                  <Reveal>
                    <span className="font-heading font-extrabold text-copper text-xl">0{i + 1}</span>
                    <h2 className="h-display text-3xl sm:text-4xl lg:text-5xl mt-2">{s.title}</h2>
                    <p className="mt-5 text-ink/85 leading-relaxed text-base sm:text-lg">{s.description}</p>
                  </Reveal>
                  <Stagger className="mt-7 grid sm:grid-cols-2 gap-3">
                    {s.items.map((it) => (
                      <StaggerItem key={it} className="flex items-start gap-3 bg-white rounded-xl border border-line px-4 py-3">
                        <Check size={18} className="text-copper shrink-0 mt-0.5" /><span className="text-sm font-medium text-slate">{it}</span>
                      </StaggerItem>
                    ))}
                  </Stagger>
                  <Reveal delay={0.1} className="mt-8">
                    <Link to={`/contact?prestation=${encodeURIComponent(s.formValue)}`} className="btn-primary" data-testid={`service-devis-button-${s.slug}`}>
                      Demander un devis pour ce service <ArrowRight size={18} />
                    </Link>
                  </Reveal>
                </div>
              </article>
            );
          })}
        </div>
      </section>

      <section id="zone-intervention" className="section bg-white scroll-mt-32" data-testid="service-area-section">
        <div className="container-x grid lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-5">
            <p className="eyebrow mb-5">Zone d'intervention</p>
            <h2 className="h-display text-4xl sm:text-5xl">Nogent-sur-Marne et <span className="text-copper">toute l'Île-de-France</span></h2>
            <p className="mt-5 text-muted leading-relaxed">Notre siège est à Nogent-sur-Marne (94). Nous intervenons dans les huit départements franciliens. Les interventions hors Île-de-France sont étudiées au cas par cas.</p>
          </Reveal>
          <div className="lg:col-span-7">
            <Stagger className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {SITE.serviceArea.filter((d) => !d.main).map((d) => (
                <StaggerItem key={d.name} className="card card-hover p-5">
                  <p className="font-heading font-extrabold text-4xl text-copper">{d.code}</p>
                  <p className="text-sm font-semibold text-slate mt-1">{d.name}</p>
                </StaggerItem>
              ))}
            </Stagger>
            <Reveal className="mt-8 card p-6">
              <p className="flex items-center gap-2 font-heading font-bold uppercase text-lg text-slate mb-3"><MapPin size={18} className="text-copper" /> Villes desservies autour de notre siège (94)</p>
              <ul className="flex flex-wrap gap-2" data-testid="service-area-cities">
                {SITE.cities94.map((c) => <li key={c} className="text-sm bg-concrete rounded-full px-3 py-1.5 text-slate">{c}</li>)}
              </ul>
            </Reveal>
          </div>
        </div>
      </section>

      <section id="faq" className="section bg-concrete scroll-mt-32" data-testid="faq-section">
        <div className="container-x grid lg:grid-cols-12 gap-12">
          <Reveal className="lg:col-span-4">
            <p className="eyebrow mb-5">FAQ</p>
            <h2 className="h-display text-4xl sm:text-5xl">Vos questions, <span className="text-copper">nos réponses</span></h2>
            <p className="mt-5 text-muted">Une autre question ? <Link to="/contact" className="text-copper font-semibold underline underline-offset-2">Contactez-nous</Link>.</p>
          </Reveal>
          <Reveal className="lg:col-span-8" delay={0.1}>
            <Accordion type="single" collapsible className="card divide-y divide-line px-6">
              {FAQ.map((f, i) => (
                <AccordionItem key={f.q} value={`faq-${i}`} className="border-0" data-testid={`faq-item-${i}`}>
                  <AccordionTrigger className="text-left font-heading font-bold text-lg sm:text-xl text-slate hover:no-underline hover:text-copper py-5" data-testid={`faq-trigger-${i}`}>{f.q}</AccordionTrigger>
                  <AccordionContent className="text-ink/85 leading-relaxed text-base pb-6">{f.a}</AccordionContent>
                </AccordionItem>
              ))}
            </Accordion>
          </Reveal>
        </div>
      </section>
      <FinalCta />
    </>
  );
}
